/**
 * Photos iPhone (HEIC/HEIF) : conversion en JPEG dans le navigateur, avant tout upload.
 *
 * **Pourquoi convertir.** Aucune étape en aval ne lit le HEIC : Chrome, Firefox et Edge ne
 * l'affichent pas dans un `<img>` (seul Safari 17+ le fait), pdf-lib n'embarque que PNG et
 * JPEG (bordereau, tampons), et les LLM de vision n'acceptent que jpeg/png/gif/webp.
 * Sources : https://caniuse.com/heif et https://github.com/Hopding/pdf-lib (vérifié 2026-09-11).
 * Une fois convertie, la photo suit exactement le chemin d'une image PNG/JPEG : aperçu, OCR,
 * synthèse, exports. Le backend ne reçoit donc jamais de HEIC, et sa liste blanche
 * (`apps/backend/utils/fileValidation.ts`) continue de le refuser.
 *
 * **Pourquoi dans le navigateur.** sharp précompilé ne décode pas le HEIC (brevets HEVC).
 * Source : https://sharp.pixelplumbing.com/install (vérifié 2026-09-11). Côté backend, il
 * aurait fallu un décodeur WASM dans un `worker_thread`, au sein d'un process API
 * mono-instance déjà tombé en OOM sur un parse lourd (#910, #912). Le poste de l'avocat
 * absorbe ce coût, isolé par onglet, et l'app web est la seule surface qui accepte des images.
 *
 * **Pourquoi JPEG et pas PNG.** Une photo en PNG sans perte pèse plusieurs fois son JPEG et
 * frôlerait la limite de 50 Mo par fichier ; la qualité 0,92 garde net le texte d'un document
 * photographié pour l'OCR. Les métadonnées EXIF (date de prise de vue, GPS) ne suivent pas :
 * l'encodage passe par un canvas. L'orientation, elle, est appliquée aux pixels : un iPhone
 * range une photo prise en portrait en paysage avec une rotation `irot`, et libheif la
 * redresse avant l'encodage (vérifié le 2026-09-11 dans Chromium sur un HEIC ImageIO portant
 * une orientation 6 : JPEG rendu en 300×600, repères visuels tournés).
 *
 * `heic-to` (libheif compilé en WASM, ~3 Mo) n'est chargé qu'au premier HEIC rencontré, dans
 * un chunk à part. Variante `/csp` : sans `new Function`, elle reste valable si la CSP de
 * l'app ajoute un jour un `script-src` sans `unsafe-eval`.
 */
import { getFileType } from '@/utils/fileProcessing';

const HEIC_EXTENSIONS: ReadonlySet<string> = new Set(['heic', 'heif']);

/**
 * Types MIME HEIC/HEIF enregistrés à l'IANA. Le type annoncé par le navigateur n'est qu'un
 * indice : Chrome le demande à l'OS et obtient `""` quand l'OS ne connaît pas `.heic`.
 * L'extension reste donc le critère principal, comme partout dans `fileProcessing`.
 * Source : https://www.iana.org/assignments/media-types/image/heic (vérifié 2026-09-11).
 */
const HEIC_MIME_TYPES: ReadonlySet<string> = new Set([
  'image/heic',
  'image/heif',
  'image/heic-sequence',
  'image/heif-sequence',
]);

const JPEG_QUALITY = 0.92;

/**
 * Borne d'une conversion. La promesse de heic-to ne se règle que sur une réponse de son
 * Worker : si le Worker meurt (mémoire épuisée sur une très grosse photo), elle ne se règle
 * jamais, et la file ci-dessous figerait tout le dépôt. Une image synthétique de 12 Mpx se
 * convertit en 0,4 s (Chromium, 2026-09-11) : une minute laisse une marge large aux photos
 * de 48 Mpx et aux postes lents.
 */
const CONVERSION_TIMEOUT_MS = 60_000;

/** Vrai pour une photo HEIC/HEIF, reconnue à son extension ou, à défaut, à son type MIME. */
export function isHeicFile(file: File): boolean {
  return HEIC_EXTENSIONS.has(getFileType(file.name)) || HEIC_MIME_TYPES.has(file.type.toLowerCase());
}

/** `IMG_1234.HEIC` → `IMG_1234.jpg` : le nom décrit le fichier réellement envoyé. */
export function toJpegFileName(fileName: string): string {
  const lastDot = fileName.lastIndexOf('.');
  const baseName = lastDot > 0 ? fileName.slice(0, lastDot) : fileName;
  return `${baseName}.jpg`;
}

/**
 * Échec de la conversion locale d'une photo HEIC. Ce n'est ni une panne réseau ni une erreur
 * Hector : le message nomme la photo et le geste qui débloque l'avocat. Rejouer l'envoi
 * échouerait de la même façon.
 */
export class HeicConversionError extends Error {
  readonly fileName: string;

  constructor(fileName: string, cause: unknown) {
    super(
      `La photo « ${fileName} » (format HEIC d'iPhone) n'a pas pu être convertie en JPEG (${describeCause(cause)}). ` +
        `Exportez-la en JPEG, par exemple depuis l'app Photos du Mac (Fichier › Exporter), puis déposez-la à nouveau.`,
      { cause },
    );
    this.name = 'HeicConversionError';
    this.fileName = fileName;
  }
}

/**
 * Raison lisible d'un échec. heic-to ne rejette pas toujours avec une `Error` : son Worker
 * renvoie une chaîne « Error: … », et un rejet sans valeur afficherait « (undefined) ».
 */
function describeCause(cause: unknown): string {
  const raw = cause instanceof Error ? cause.message : typeof cause === 'string' ? cause : '';
  const detail = raw.replace(/^Error:\s*/, '').trim();
  return detail || "le décodeur n'a donné aucun détail";
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error(
        `aucune réponse du décodeur après ${timeoutMs / 1000} s, rechargez la page avant de réessayer`,
      )),
      timeoutMs,
    );
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

/**
 * Une conversion à la fois. `batchUploadFiles` envoie jusqu'à 6 fichiers en parallèle, et
 * chaque décodage retient l'image entière en mémoire, à 4 octets par pixel : 48 Mo pour une
 * photo de 12 Mpx, 192 Mo pour 48 Mpx. Six décodages simultanés pourraient faire tomber
 * l'onglet au milieu d'un dépôt ; en série, l'envoi d'une photo convertie avance pendant que
 * la suivante se convertit.
 */
let conversionQueue: Promise<unknown> = Promise.resolve();

function runOneAtATime<T>(task: () => Promise<T>): Promise<T> {
  const run = conversionQueue.then(task);
  conversionQueue = run.catch(() => undefined);
  return run;
}

/**
 * Convertit une photo HEIC/HEIF en JPEG.
 *
 * @throws {HeicConversionError} si le chargement du décodeur ou le décodage échoue.
 */
export function convertHeicToJpeg(file: File): Promise<File> {
  return runOneAtATime(async () => {
    try {
      const { heicTo } = await import('heic-to/csp');
      const jpeg = await withTimeout(
        heicTo({ blob: file, type: 'image/jpeg', quality: JPEG_QUALITY }),
        CONVERSION_TIMEOUT_MS,
      );
      return new File([jpeg], toJpegFileName(file.name), {
        type: 'image/jpeg',
        lastModified: file.lastModified,
      });
    } catch (error) {
      throw new HeicConversionError(file.name, error);
    }
  });
}

/**
 * Remplace chaque photo HEIC d'une sélection par son JPEG, dans l'ordre d'origine. Les autres
 * fichiers passent tels quels. Une photo qui ne se convertit pas est retirée et son erreur
 * rendue à l'appelant : une seule photo illisible ne bloque pas les autres.
 */
export async function convertHeicFilesToJpeg(
  files: File[],
): Promise<{ files: File[]; failures: HeicConversionError[] }> {
  const settled = await Promise.allSettled(
    files.map((file) => (isHeicFile(file) ? convertHeicToJpeg(file) : Promise.resolve(file))),
  );
  const converted: File[] = [];
  const failures: HeicConversionError[] = [];
  for (const outcome of settled) {
    if (outcome.status === 'fulfilled') {
      converted.push(outcome.value);
    } else if (outcome.reason instanceof HeicConversionError) {
      failures.push(outcome.reason);
    } else {
      throw outcome.reason;
    }
  }
  return { files: converted, failures };
}
